// web3.js doubles its wait between rate-limit retries starting at 500 ms, and
// the trade builders issue 2–3 RPC reads back to back, so five attempts per
// read leave the user on a spinner for ~20 s before any error surfaces. Two
// retries (1.5 s) cover a transient limit; a saturated endpoint should fail
// visibly instead.
const WEB3_RATE_LIMIT_ATTEMPTS = 3;

interface RpcRequestIdentity {
  key: string;
  method: string | null;
}

interface SolanaRpcRequestErrorArgs {
  method: string | null;
  httpStatus: number | null;
  retryAfter: string | null;
  rpcRetryCount: number;
  cause: Error | undefined;
}

export interface RpcFailureLogContext {
  rpcMethod: string | null;
  httpStatus: number | null;
  retryAfter: string | null;
  rpcRetryCount: number;
}

export class SolanaRpcRequestError extends Error {
  readonly method: string | null;
  readonly httpStatus: number | null;
  readonly retryAfter: string | null;
  readonly rpcRetryCount: number;

  constructor(args: SolanaRpcRequestErrorArgs) {
    super(
      args.httpStatus === null
        ? `Solana RPC ${args.method ?? 'unknown'} request failed`
        : `Solana RPC ${args.method ?? 'unknown'} request failed with HTTP ${args.httpStatus}`,
      { cause: args.cause },
    );
    this.name = 'SolanaRpcRequestError';
    this.method = args.method;
    this.httpStatus = args.httpStatus;
    this.retryAfter = args.retryAfter;
    this.rpcRetryCount = args.rpcRetryCount;
  }
}

/**
 * Preserves web3.js's built-in 429 retry loop while retaining the transport
 * context that its terminal Error otherwise discards.
 */
export function makeRpcObservabilityFetch(delegateFetch: typeof fetch): typeof fetch {
  const rateLimitAttempts = new Map<string, number>();

  return async function rpcObservabilityFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const identity = rpcRequestIdentity(init?.body);
    let response: Response;
    try {
      response = await delegateFetch(input, init);
    } catch (cause) {
      throw new SolanaRpcRequestError({
        method: identity?.method ?? null,
        httpStatus: null,
        retryAfter: null,
        rpcRetryCount: 0,
        cause: cause instanceof Error ? cause : new Error(String(cause)),
      });
    }

    if (response.ok) {
      if (identity !== null) rateLimitAttempts.delete(identity.key);
      return response;
    }

    const retryAfter = response.headers.get('retry-after');
    if (response.status !== 429 || identity === null) {
      throw new SolanaRpcRequestError({
        method: identity?.method ?? null,
        httpStatus: response.status,
        retryAfter,
        rpcRetryCount: 0,
        cause: undefined,
      });
    }

    const attempt = (rateLimitAttempts.get(identity.key) ?? 0) + 1;
    if (attempt < WEB3_RATE_LIMIT_ATTEMPTS) {
      rateLimitAttempts.set(identity.key, attempt);
      return response;
    }

    rateLimitAttempts.delete(identity.key);
    throw new SolanaRpcRequestError({
      method: identity.method,
      httpStatus: response.status,
      retryAfter,
      rpcRetryCount: attempt - 1,
      cause: undefined,
    });
  };
}

export function rpcFailureLogContext(error: Error | null): RpcFailureLogContext {
  if (error instanceof SolanaRpcRequestError) {
    return {
      rpcMethod: error.method,
      httpStatus: error.httpStatus,
      retryAfter: error.retryAfter,
      rpcRetryCount: error.rpcRetryCount,
    };
  }
  return {
    rpcMethod: null,
    httpStatus: httpStatusFromError(error),
    retryAfter: null,
    rpcRetryCount: 0,
  };
}

function rpcRequestIdentity(body: BodyInit | null | undefined): RpcRequestIdentity | null {
  if (typeof body !== 'string') return null;
  let parsed: Record<string, string | number>;
  try {
    parsed = JSON.parse(body) as Record<string, string | number>;
  } catch {
    // SILENT-CATCH: non-JSON requests have no safe RPC identity metadata to attach.
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const method = typeof parsed.method === 'string' ? parsed.method : null;
  const id =
    typeof parsed.id === 'string' || typeof parsed.id === 'number' ? String(parsed.id) : body;
  return { key: `${method ?? 'unknown'}:${id}`, method };
}

function httpStatusFromError(error: Error | null): number | null {
  if (error === null) return null;
  const match = /^(\d{3})\s/.exec(error.message);
  if (match === null) return null;
  const status = Number(match[1]);
  return Number.isInteger(status) ? status : null;
}
